import {logger, NetUtil, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  UserCardEventReq,
  UserCardIdReq,
  UserCardIdResp,
  UserStatsEvent
} from 'yougile-model'
import {Backend} from './backend'

const cardIdKey = 'card_id'
const cardSessionKey = 'card_session'
const sessionTtl = 24 * 3600 * 1000

const getRefFromUrl = (): string => {
  const path = location.pathname
  if (path.startsWith('/p/')) {
    return path.split('/p/')[1].split('/')[0]
  }
  const query = location.search
  if (query.includes('?p=')) {
    return query.split('?p=')[1].split('&')[0]
  }
  if (query.includes('&p=')) {
    return query.split('&p=')[1].split('&')[0]
  }
  return undefined
}
const cleanRefFromUrl = () => {
  const ref = getRefFromUrl()
  if (!ref) {
    return
  }
  const href = location.href
    .replace(`/p/${ref}`, '')
    .replace(`?p=${ref}&`, '?')
    .replace(`?p=${ref}`, '')
    .replace(`&p=${ref}`, '')
  window.history.replaceState({reason: 'ref'}, document.title, href)
}

class UserCardClientInstance {
  private eventQueue: UserStatsEvent[] = []
  private cardId: string
  private ref: string

  constructor() {
    const key = Backend.loadKey()
    if (key?.endsWith('_')) {
      return
    }
    if (Util.isDesktop()) {
      return
    }
    this.ref = getRefFromUrl()
    this.saveRef()
    if (
      !this.ref &&
      location.href.includes('utm_source=yandex') &&
      location.href.includes('utm_medium=cpc') &&
      location.href.includes('utm_campaign=')
    ) {
      Backend.report(`Yandex campaign without ref link ${location.href}`)
    }
    cleanRefFromUrl()

    this.sendEvent({type: 'pageload'})
    this.checkReferrer()
    this.checkCampaign()

    const savedCardId = Backend.loadLocal(cardIdKey)
    const userId = Backend.loadId()
    const req: UserCardIdReq = {
      cardId: savedCardId,
      userId
    }
    Backend.postJSON<UserCardIdResp>('/data/card/id', req)
      .then(res => {
        if (res.cardId) {
          this.cardId = res.cardId
          Backend.saveLocal(cardIdKey, res.cardId)
          const queue = [...this.eventQueue]
          this.eventQueue = []
          queue.forEach(e => this.sendEvent(e))
        }
      })
      .catch(e => logger.warn(e))
  }

  private saveRef(): void {
    try {
      if (this.ref) {
        localStorage.setItem(
          'p',
          window.btoa(JSON.stringify({link: this.ref, time: Time.now()}))
        )
      }
    } catch (e) {
      logger.error(e)
    }
  }

  private loadRef(): void {
    try {
      const data = localStorage.getItem('p')
      if (!data) {
        return
      }
      const {link} = JSON.parse(window.atob(data)) as {
        link: string
        time: number
      }
      if (link) {
        this.ref = link
      }
    } catch (e) {
      logger.error(e)
    }
  }

  private checkReferrer(): void {
    if (!document.referrer) {
      return
    }
    const session = this.loadSession()
    if (
      !session ||
      session.timestamp < Date.now() - sessionTtl ||
      session.referrer !== document.referrer
    ) {
      const domain = document.referrer
        .split('//')
        .pop()
        .split('/')[0]
        .split('?')[0]
      if (domain.includes('yougile.com')) {
        return
      }
      if (domain === location.host) {
        return
      }

      this.saveSession(Date.now(), document.referrer)
      this.sendEvent({
        type: 'referrer',
        payload: document.referrer.slice(0, 512)
      })
    }
  }

  private checkCampaign(): void {
    const params = NetUtil.getLocationParams()
    if (params.cm) {
      const {cm, ...newParams} = params
      window.history.pushState({}, '', NetUtil.getUrlWithParams(newParams))
      this.sendEvent({type: 'campaign', payload: cm})
    }
  }

  private saveSession(timestamp: number, referrer: string): void {
    if (!window.btoa) {
      return
    }
    const obj = {
      timestamp,
      referrer: encodeURIComponent(referrer)
    }
    const str = window.btoa(JSON.stringify(obj))
    Backend.saveLocal(cardSessionKey, str)
  }

  private loadSession(): {timestamp: number; referrer: string} {
    if (!window.atob) {
      return undefined
    }
    const str = Backend.loadLocal(cardSessionKey)
    if (!str) {
      return undefined
    }
    try {
      return JSON.parse(window.atob(str))
    } catch (e) {
      logger.warn(e)
      return undefined
    }
  }

  public sendEvent(event: UserStatsEvent): void {
    if (!this.cardId) {
      this.eventQueue.push(event)
      return
    }
    this.loadRef()
    const req: UserCardEventReq = {cardId: this.cardId, event, ref: this.ref}
    Backend.postJSON('/data/card/event', req).catch(e => logger.warn(e))
  }

  public trackRegistration(userId: DataObjectId): void {
    const req: UserCardIdReq = {
      cardId: this.cardId,
      userId
    }
    Backend.postJSON<UserCardIdResp>('/data/card/id', req)
      .then(() => {
        this.sendEvent({type: 'registration', payload: userId})
      })
      .catch(e => logger.warn(e))
  }
}

export class UserCardClient {
  private static instance: UserCardClientInstance

  public static init(): void {
    if (UserCardClient.instance) {
      return
    }
    UserCardClient.instance = new UserCardClientInstance()
  }

  public static getInstance(): UserCardClientInstance {
    return UserCardClient.instance
  }
}
